<section class="dashboard-tabs-card" id="tour-dashboard-leads-status">
  <div class="tabs-header">
    <div class="tabs-header-content">
      <div>
        <h3 class="section-title">Leads Dashboard</h3>
        <p class="section-subtitle">
          Track Lead Status, DeepRoots, Criteria, and Links in one place.
        </p>
      </div>
      <div class="tabs-header-controls">
        @if (dashboardMetrics?.last_refreshed_at) {
          <div class="last-updated-badge" title="Last updated time">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              class="clock-icon"
            >
              <path
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>
              Last updated
              {{ lastRefreshedAtDate | date: "MMM d, y, h:mm a" }}
            </span>
          </div>
        }

        <div class="header-status-slot">
          <button
            type="button"
            class="refresh-btn"
            [class.active]="isDashboardExpanded"
            (click)="onRefreshMetrics()"
            [disabled]="isLoading"
            title="Recalculate dashboard metrics"
            aria-label="Recalculate dashboard metrics"
          >
            <svg
              class="refresh-icon"
              [class.spinning]="isLoading"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <div
            class="assigned-leads-badge"
            [class.active]="!isDashboardExpanded"
            title="Total Assigned Leads"
          >
            <span class="badge-label">Assigned Leads:</span>
            <span class="badge-count">{{ totalAssignedLeads | number }}</span>
          </div>
        </div>

        @if (canAddLead) {
          <app-add-business-record
            [buttonLabel]="'Add Lead'"
            [fullWidth]="false"
            (addedBusiness)="onLeadAdded($event)"
          ></app-add-business-record>
        }
        <button
          type="button"
          class="toggle-btn"
          (click)="toggleDashboard()"
          [attr.aria-label]="
            isDashboardExpanded
              ? 'Collapse lead dashboard'
              : 'Expand lead dashboard'
          "
          [attr.aria-expanded]="isDashboardExpanded"
        >
          <svg
            class="toggle-icon"
            [class.expanded]="isDashboardExpanded"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M19 9l-7 7-7-7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>

  @if (isDashboardExpanded) {
    <div class="tabs-nav" role="tablist" aria-label="Dashboard sections">
      @for (tab of tabs; track tab.id) {
        <button
          type="button"
          class="tab-button"
          [class.active]="activeTab === tab.id"
          [attr.aria-selected]="activeTab === tab.id"
          (click)="selectTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      }
    </div>

    @if (isLoading && !dashboardMetrics) {
      <div class="dashboard-loader">
        <div class="loader-content">
          <div class="loader-spinner"></div>
          <p class="loader-text">Loading dashboard metrics...</p>
        </div>
      </div>
    } @else {
      <div class="tab-panel">
        @if (activeTab === "lead-status") {
          <app-dashboard-leads-status
            [dashboardMetrics]="dashboardMetrics"
            (leadsToastMessage)="onLeadsToastMessage($event)"
          ></app-dashboard-leads-status>
        }

        @if (activeTab === "deeproots") {
          <div class="summary-panel">
            <div class="summary-hero-dual">
              <div class="summary-metrics-row">
                <div class="summary-metric">
                  <div class="summary-label">Total Matches</div>
                  <div class="summary-total">
                    {{ matchedRecordsSummary.total }}
                  </div>
                </div>
                <div class="summary-metric">
                  <div class="summary-label">Total Inquiries</div>
                  <div class="summary-total">{{ inquiriesSummary.total }}</div>
                </div>
              </div>
              <button
                type="button"
                class="panel-action"
                (click)="navigateTo('leads/find?tab=deeproots&subtab=matched')"
              >
                <span>View DeepRoots Leads</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M13 7L18 12M18 12L13 17M18 12H6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div class="summary-section-title">Business Support Options</div>
            @if (deepRootsChartItems.length > 0) {
              <div class="deep-roots-chart">
                <div class="deep-roots-plot">
                  <div class="deep-roots-y-axis">
                    @for (tick of deepRootsChartAxisTicksDesc; track $index) {
                      <span>{{ tick }}</span>
                    }
                  </div>

                  <div class="deep-roots-columns-wrap">
                    <div class="deep-roots-columns">
                      @for (item of deepRootsChartItems; track item.label) {
                        <div class="deep-roots-group">
                          <div class="deep-roots-bars">
                            <div
                              class="deep-roots-bar inquiries"
                              [class.animate]="isDeepRootsBarsAnimated"
                              [style.height.%]="
                                getDeepRootsBarHeight(item.inquiries)
                              "
                            >
                              <span
                                class="deep-roots-bar-value"
                                style="margin-top: 6px"
                                [class.animate]="isDeepRootsBarsAnimated"
                              >
                                {{ item.inquiries }}
                              </span>
                            </div>

                            <div
                              class="deep-roots-bar matches"
                              [class.animate]="isDeepRootsBarsAnimated"
                              [style.height.%]="
                                getDeepRootsBarHeight(item.matches)
                              "
                            >
                              <span
                                class="deep-roots-bar-value"
                                [class.animate]="isDeepRootsBarsAnimated"
                              >
                                {{ item.matches }}
                              </span>
                            </div>
                          </div>
                        </div>
                      }
                    </div>

                    <div class="deep-roots-x-axis-labels">
                      @for (item of deepRootsChartItems; track item.label) {
                        <div
                          class="deep-roots-x-label"
                          [attr.title]="item.label"
                        >
                          {{ item.label }}
                        </div>
                      }
                    </div>
                  </div>
                </div>

                <div class="deep-roots-chart-legend">
                  <div class="legend-item">
                    <span class="legend-dot inquiries"></span>
                    <span>Inquiries</span>
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot matches"></span>
                    <span>Matches</span>
                  </div>
                </div>
              </div>
            } @else {
              <div class="empty-summary">
                No business support data found yet.
              </div>
            }
          </div>
        }

        @if (activeTab === "lead-criteria") {
          <div class="summary-panel">
            <div class="summary-hero">
              <div class="criteria-metrics-row">
                <div class="summary-metric">
                  <div class="summary-label">Total Lead Criteria</div>
                  <div class="summary-total">
                    {{ leadCriteriaSummary.total }}
                  </div>
                </div>
                <div class="summary-metric">
                  <div class="summary-label">Total Matches</div>
                  <div class="summary-total">
                    {{ matchedRecordsSummary.total }}
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="panel-action"
                (click)="navigateTo('/deeproots/leads-criteria')"
              >
                <span>View Lead Criteria</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M13 7L18 12M18 12L13 17M18 12H6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div class="summary-section-title">
              Active Lead Criteria & Match Distribution
            </div>
            @if (leadCriteriaSummary.items.length > 0) {
              <div class="criteria-analytics">
                <div class="criteria-card-grid">
                  @for (
                    item of leadCriteriaSummary.items;
                    track trackByLabel($index, item);
                    let i = $index
                  ) {
                    <div
                      class="criteria-card"
                      [class.high-match]="
                        getSummaryItemPercentage(
                          leadCriteriaSummary,
                          item.count
                        ) >= 90
                      "
                      [class.animate]="isCriteriaChartAnimated"
                      [style.--card-delay.ms]="i * 60"
                      tabindex="0"
                      role="button"
                      (click)="onCriteriaCardClick($event, item)"
                      (keyup.enter)="onCriteriaCardClick($event, item)"
                    >
                      <div class="criteria-card-header">
                        <div class="criteria-card-info">
                          <span
                            class="criteria-card-dot"
                            [style.background]="getCriteriaColor(i, item.count)"
                          ></span>
                          <span
                            class="criteria-card-title"
                            [attr.title]="item.label"
                          >
                            {{ item.label }}
                          </span>
                        </div>

                        <div class="criteria-card-metrics">
                          @if (
                            getSummaryItemPercentage(
                              leadCriteriaSummary,
                              item.count
                            ) >= 90
                          ) {
                            <span
                              class="criteria-card-badge high-match"
                              title="High Match (>=90%)"
                            >
                              🎉 High Match
                            </span>
                          }
                          <span class="criteria-card-count">
                            {{ item.count }}
                            {{ item.count === 1 ? "match" : "matches" }}
                          </span>
                          <span class="criteria-card-pct">
                            {{
                              getSummaryItemPercentage(
                                leadCriteriaSummary,
                                item.count
                              )
                            }}%
                          </span>
                        </div>
                      </div>

                      <div class="criteria-card-bar-track">
                        <div
                          class="criteria-card-bar-fill"
                          [class.animate]="isCriteriaChartAnimated"
                          [style.--card-delay.ms]="i * 60 + 80"
                          [style.--fill-scale]="
                            getSummaryItemPercentage(
                              leadCriteriaSummary,
                              item.count
                            ) / 100
                          "
                          [style.background]="getCriteriaColor(i, item.count)"
                        ></div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            } @else {
              <div class="empty-summary">
                No lead criteria activity found yet.
              </div>
            }
          </div>
        }

        @if (activeTab === "links") {
          <div class="summary-panel">
            <div class="summary-hero-dual">
              <div class="summary-metrics-row">
                <div class="summary-metric">
                  <div class="summary-label">Total Links</div>
                  <div class="summary-total">
                    {{ linksSummary.total }}
                  </div>
                </div>
                <div class="summary-metric">
                  <div class="summary-label">Total Inquiries</div>
                  <div class="summary-total">{{ totalInquiriesReceived }}</div>
                </div>
              </div>
              <button
                type="button"
                class="panel-action"
                (click)="navigateTo('/deeproots/links')"
              >
                <span>View Links</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M13 7L18 12M18 12L13 17M18 12H6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div class="summary-section-title">Submissions by Link</div>
            @if (linksChartItems.length > 0) {
              <div class="links-chart">
                <div class="links-plot">
                  <div class="links-y-axis">
                    @for (tick of linksChartAxisTicksDesc; track $index) {
                      <span>{{ tick }}</span>
                    }
                  </div>

                  <div class="links-columns-wrap">
                    <div class="links-columns">
                      @for (item of linksChartItems; track item.label) {
                        <div class="links-group">
                          <div class="links-bar-container">
                            <div
                              class="links-bar"
                              [class.animate]="isLinksChartAnimated"
                              [style.height.%]="
                                getLinksBarHeight(item.submissions)
                              "
                            >
                              <span
                                class="links-bar-value"
                                [class.animate]="isLinksChartAnimated"
                              >
                                {{ item.submissions }}
                              </span>
                              <span
                                class="links-bar-percent"
                                [class.animate]="isLinksChartAnimated"
                              >
                                {{
                                  getSummaryItemPercentage(
                                    linksSummary,
                                    item.submissions
                                  )
                                }}%
                              </span>
                            </div>
                          </div>
                        </div>
                      }
                    </div>

                    <div class="links-x-axis-labels">
                      @for (item of linksChartItems; track item.label) {
                        <div class="links-x-label" [attr.title]="item.label">
                          {{ item.label }}
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            } @else {
              <div class="empty-summary">
                No link submission data found yet.
              </div>
            }
          </div>
        }
      </div>
    }
  }
</section>
